import { type FixedDepartureDetail, type FixedDepartureHotelOut } from '@utazzo/shared';
import { UzButton, UzEmptyState, UzInput, UzNumberInput, UzSelect } from '@utazzo/ui';
import { BedDouble, LayoutGrid, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

import {
  useCreateFixedDepartureHotel,
  useDeleteFixedDepartureHotel,
  useUpdateFixedDepartureHotel,
} from '../use-fixed-departures';

import { CityPicker } from '@/components/pickers';
import { useOccupancyTypesList } from '@/features/occupancy-types/use-occupancy-types';

const NONE = '__none__';

interface HotelDraft {
  categoryId: string;
  cityId: string | null;
  hotelName: string;
  nights: number;
  occupancyTypeId: string;
}

const emptyDraft = (categoryId: string): HotelDraft => ({
  categoryId,
  cityId: null,
  hotelName: '',
  nights: 1,
  occupancyTypeId: NONE,
});

const toDraft = (h: FixedDepartureHotelOut): HotelDraft => ({
  categoryId: h.categoryId,
  cityId: h.cityId,
  hotelName: h.hotelName,
  nights: h.nights,
  occupancyTypeId: h.occupancyTypeId ?? NONE,
});

const toPayload = (d: HotelDraft) => ({
  categoryId: d.categoryId,
  cityId: d.cityId as string,
  hotelName: d.hotelName.trim(),
  nights: d.nights,
  occupancyTypeId: d.occupancyTypeId === NONE ? null : d.occupancyTypeId,
});

/**
 * Hotel OPTION POOL, grouped by grade tier - the shape a vendor sheet prints
 * it in: under "Premium", one night on a Premium Houseboat plus three nights
 * at Hotel Arison Luxury.
 *
 * Hotel names are free text on purpose: a rate sheet routinely lists a
 * property that is not in the hotel master, usually with a "/ similar" escape
 * hatch that has to survive verbatim.
 */
export const FdHotelsStep = ({ fd }: { fd: FixedDepartureDetail }): React.JSX.Element => {
  const createHotel = useCreateFixedDepartureHotel(fd.id);
  const updateHotel = useUpdateFixedDepartureHotel(fd.id);
  const deleteHotel = useDeleteFixedDepartureHotel(fd.id);
  const occupancies = useOccupancyTypesList({
    page: 1,
    pageSize: 100,
    sortDir: 'asc',
    isActive: true,
  });

  /** Which tier's "add hotel" form is open, if any. */
  const [addingFor, setAddingFor] = useState<string | null>(null);
  const [draft, setDraft] = useState<HotelDraft>(() => emptyDraft(''));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<HotelDraft>(() => emptyDraft(''));

  const occupancyOptions = [
    { value: NONE, label: 'Any occupancy' },
    ...(occupancies.data?.data ?? []).map((o) => ({ value: o.id, label: o.name })),
  ];
  const categoryOptions = fd.categories.map((c) => ({ value: c.id, label: c.name }));

  const valid = (d: HotelDraft): boolean =>
    Boolean(d.categoryId) && Boolean(d.cityId) && d.hotelName.trim().length > 0;

  const submitNew = async (): Promise<void> => {
    if (!valid(draft)) return;
    await createHotel.mutateAsync(toPayload(draft));
    setDraft(emptyDraft(draft.categoryId));
    setAddingFor(null);
  };

  const submitEdit = async (): Promise<void> => {
    if (!editingId || !valid(editDraft)) return;
    await updateHotel.mutateAsync({ hotelId: editingId, payload: toPayload(editDraft) });
    setEditingId(null);
  };

  const form = (
    d: HotelDraft,
    set: (patch: Partial<HotelDraft>) => void,
    showCategory: boolean,
  ): React.JSX.Element => (
    <div
      className={
        'grid gap-3 sm:grid-cols-2 ' +
        (showCategory
          ? 'lg:grid-cols-[1fr_1.2fr_1.4fr_90px_1fr]'
          : 'lg:grid-cols-[1.2fr_1.4fr_90px_1fr]')
      }
    >
      {showCategory && (
        <UzSelect
          label="Tier"
          value={d.categoryId}
          onValueChange={(v) => set({ categoryId: v })}
          options={categoryOptions}
        />
      )}
      <div>
        {/* <p className="mb-1 text-[10px] text-zinc-400 uppercase">City</p> */}
        <CityPicker label={undefined} value={d.cityId} onValueChange={(v) => set({ cityId: v })} />
      </div>
      <UzInput
        label="Hotel name"
        placeholder="e.g. Hotel Arison Luxury / similar"
        value={d.hotelName}
        onChange={(e) => set({ hotelName: e.target.value })}
      />
      <UzNumberInput
        label="Nights"
        precision={0}
        min={1}
        max={60}
        value={d.nights}
        onValueChange={(v) => set({ nights: v ?? 1 })}
      />
      <UzSelect
        label="Occupancy"
        value={d.occupancyTypeId}
        onValueChange={(v) => set({ occupancyTypeId: v })}
        options={occupancyOptions}
      />
    </div>
  );

  if (fd.categories.length === 0) {
    return (
      <UzEmptyState
        icon={<LayoutGrid className="h-6 w-6" aria-hidden />}
        title="Add a grade tier first"
        description="Hotels belong to a tier, because that is where their price lives - a rate sheet quotes one hotel set and one price column per tier. Create the tiers on the Tiers & Pricing step, then come back."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-sm font-semibold text-zinc-900">Hotel options by tier</h2>
        <p className="mt-0.5 text-xs text-zinc-500">Every property this itinerary could use.</p>
      </div>

      {fd.categories.map((category) => {
        const hotels = fd.hotels.filter((h) => h.categoryId === category.id);
        const nights = hotels.reduce((sum, h) => sum + h.nights, 0);
        const nightsMismatch = fd.numberOfNights > 0 && nights > 0 && nights !== fd.numberOfNights;
        return (
          <section key={category.id} className="rounded-lg border border-zinc-200 bg-white">
            <header className="flex items-center justify-between gap-3 border-b border-zinc-100 px-5 py-3">
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-semibold text-zinc-900">{category.name}</span>
                {category.starLabel && (
                  <span className="text-xs text-zinc-500">{category.starLabel}</span>
                )}
                <span
                  className={'text-xs ' + (nightsMismatch ? 'text-amber-600' : 'text-zinc-400')}
                >
                  {nights} of {fd.numberOfNights} nights covered
                </span>
              </div>
              {addingFor !== category.id && (
                <UzButton
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setAddingFor(category.id);
                    setDraft(emptyDraft(category.id));
                  }}
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                  Add hotel
                </UzButton>
              )}
            </header>

            <div className="space-y-2 p-5">
              {hotels.length === 0 && addingFor !== category.id && (
                <p className="py-2 text-sm text-zinc-400">No hotels on this tier yet.</p>
              )}

              {hotels.map((hotel) =>
                editingId === hotel.id ? (
                  <div key={hotel.id} className="rounded-lg border border-zinc-300 p-4">
                    {form(editDraft, (patch) => setEditDraft((p) => ({ ...p, ...patch })), true)}
                    <div className="mt-3 flex items-center gap-2">
                      <UzButton
                        size="sm"
                        onClick={submitEdit}
                        disabled={!valid(editDraft)}
                        loading={updateHotel.isPending}
                      >
                        Save
                      </UzButton>
                      <UzButton size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                        Cancel
                      </UzButton>
                    </div>
                  </div>
                ) : (
                  <div
                    key={hotel.id}
                    className="flex items-center gap-3 rounded-lg border border-zinc-200 px-4 py-2.5"
                  >
                    <BedDouble className="h-4 w-4 shrink-0 text-zinc-300" aria-hidden />
                    <button
                      type="button"
                      className="min-w-0 flex-1 text-left"
                      onClick={() => {
                        setEditingId(hotel.id);
                        setEditDraft(toDraft(hotel));
                      }}
                    >
                      <div className="truncate text-sm font-medium text-zinc-900">
                        {hotel.nights} {hotel.nights === 1 ? 'Night' : 'Nights'} at{' '}
                        {hotel.hotelName}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-zinc-500">
                        <span>{hotel.cityName ?? 'Unknown city'}</span>
                        {hotel.occupancyTypeName && (
                          <>
                            <span className="text-zinc-300">|</span>
                            <span>{hotel.occupancyTypeName}</span>
                          </>
                        )}
                      </div>
                    </button>
                    <UzButton
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-zinc-400 hover:text-red-600"
                      onClick={() => deleteHotel.mutate(hotel.id)}
                      aria-label={`Remove ${hotel.hotelName}`}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </UzButton>
                  </div>
                ),
              )}

              {addingFor === category.id && (
                <div className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-4">
                  {form(draft, (patch) => setDraft((p) => ({ ...p, ...patch })), false)}
                  <div className="mt-3 flex items-center gap-2">
                    <UzButton
                      size="sm"
                      onClick={submitNew}
                      disabled={!valid(draft)}
                      loading={createHotel.isPending}
                    >
                      Add hotel
                    </UzButton>
                    <UzButton size="sm" variant="ghost" onClick={() => setAddingFor(null)}>
                      Cancel
                    </UzButton>
                  </div>
                </div>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
};
